import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Cleaner,
  CleanerProfile,
  Cleaners,
  CleanersState,
  EditingCleaner,
  Favorites,
  NewCleaner,
  Slots,
} from './model';
import { CLEANERS_ACTIONS, CLEANER_LOADERS } from './utils';
import { User } from '@supabase/supabase-js';

const initialState: CleanersState = {
  loaders: {},
  pagination: {
    currentPage: 1,
  },
};

export const cleanersSlice = createSlice({
  name: 'cleaners',
  initialState,
  reducers: {
    reset: () => initialState,
    updateNewCleaner: <K extends keyof NewCleaner>(
      state,
      {
        payload: { key, value },
      }: PayloadAction<{ key: K; value: NewCleaner[K] }>,
    ) => {
      state.newCleaner = { ...(state.newCleaner ?? {}), [key]: value };
    },
    toggleLoader: (
      state,
      {
        payload: { key, value },
      }: PayloadAction<{ key: CLEANER_LOADERS; value: boolean }>,
    ) => {
      state.loaders[key] = value;
    },
    setCleaners: (state, { payload }: PayloadAction<Cleaners>) => {
      state.cleaners = payload;
    },
    setCurrentCleanerById: (state, { payload }: PayloadAction<string>) => {
      if (state.cleaners) {
        state.currentCleaner = payload
          ? state.cleaners.find(({ _id }) => _id === payload)
          : initialState.currentCleaner;
      }
    },
    setCleanerSlots: (state, { payload }: PayloadAction<Slots>) => {
      if (state.currentCleaner) {
        state.currentCleaner.slots = payload;
      }
    },
    setFavorites(state, { payload }: PayloadAction<Favorites>) {
      state.favorites = payload;
    },
    setCurrentCleaner: (state, { payload }: PayloadAction<Cleaner>) => {
      state.currentCleaner = payload;
    },
    setHasMore: (state, { payload }: PayloadAction<boolean>) => {
      state.pagination.hasMore = payload;
    },
    setCurrentPage: (state, { payload }: PayloadAction<number>) => {
      state.pagination.currentPage = payload;
    },
    setCleanerProfile: (state, { payload }: PayloadAction<CleanerProfile>) => {
      state.cleanerProfile = payload;
    },
    resetCurrentCleaner: (state) => {
      state.currentCleaner = initialState.currentCleaner;
    },
    setEditingCurrentCleaner: (
      state,
      { payload }: PayloadAction<EditingCleaner>,
    ) => {
      state.editingCleaner = payload;
    },
    resetEditingCleaner: (state) => {
      state.editingCleaner = initialState.editingCleaner;
    },
    updateEditingCleaner: <K extends keyof EditingCleaner>(
      state,
      {
        payload: { key, value },
      }: PayloadAction<{ key: K; value: EditingCleaner[K] }>,
    ) => {
      state.editingCleaner = { ...(state.editingCleaner ?? {}), [key]: value };
    },
    setCleanerVisibility: (state, { payload }: PayloadAction<boolean>) => {
      state.cleanerProfile.disabled = payload;
    },
  },
});

const extraActions = {
  searchCleaners: createAction<number>(CLEANERS_ACTIONS.SEARCH_CLEANERS),
  createCleaner: createAction(CLEANERS_ACTIONS.CREATE_CLEANER),
  getCleanerDetail: createAction<string>(CLEANERS_ACTIONS.GET_CLEANER_DETAIL),
  fetchFavorites: createAction<User>(CLEANERS_ACTIONS.FETCH_FAVORITES),
  removeFavorite: createAction<string>(CLEANERS_ACTIONS.REMOVE_FAVORITE),
  addFavorite: createAction<string>(CLEANERS_ACTIONS.ADD_FAVORITE),
  findSlots: createAction<{ cleanerId: string; date?: number }>(
    CLEANERS_ACTIONS.FIND_SLOTS,
  ),
  getCleanerProfile: createAction(CLEANERS_ACTIONS.GET_CLEANER_PROFILE),
  setEditingCleaner: createAction(CLEANERS_ACTIONS.SET_EDITING_CLEANER),
  updateCleanerData: createAction(CLEANERS_ACTIONS.UPDATE_CLEANER_DATA),
  changeCleanerVisibility: createAction<boolean>(
    CLEANERS_ACTIONS.CHANGE_CLEANER_VISIBILITY,
  ),
};
export const {
  reset,
  setFavorites,
  searchCleaners,
  createCleaner,
  updateNewCleaner,
  toggleLoader,
  setCleaners,
  setCurrentCleanerById,
  setCurrentCleaner,
  getCleanerDetail,
  setCleanerVisibility,
  fetchFavorites,
  removeFavorite,
  addFavorite,
  resetCurrentCleaner,
  findSlots,
  setCleanerSlots,
  setCurrentPage,
  setHasMore,
  getCleanerProfile,
  setCleanerProfile,
  setEditingCleaner,
  setEditingCurrentCleaner,
  resetEditingCleaner,
  updateEditingCleaner,
  updateCleanerData,
  changeCleanerVisibility,
} = { ...cleanersSlice.actions, ...extraActions };

export const cleanersReducer = cleanersSlice.reducer;
